import { useState, useMemo, useEffect, Fragment } from '@wordpress/element';
import { __, sprintf } from '@wordpress/i18n';
import { Tooltip } from '@getdokan/dokan-ui';
import { Slot } from '@wordpress/components';

import { truncate } from '@dokan/utilities';
import { useDispatch, useSelect } from '@wordpress/data';
import { VENDOR_QUOTES_STORE, QUOTE_STATUS, STATUS_LABELS } from '../constants';

import StatusTabs from './QuoteList/StatusTabs';
import EmptyQuote from './QuoteList/EmptyQuote';
import QuoteListSkeleton from './QuoteList/QuoteListSkeleton';
import { DispatchActions, QueryParams } from '../types/quote';
import QuoteStatus from './QuoteDetails/QuoteStatus';
import { QUOTE_LIST_SLOTS } from './Common/QuoteListSlot';

// Import the DataViews component from @dokan/components
import { DataViews, DokanLink, DokanModal } from '@dokan/components';
import QuoteFilter from './QuoteList/QuoteFilter';
import { dateI18n, getSettings } from '@wordpress/date';
import { CircleHelp } from 'lucide-react';

const QuoteList = ( { navigate } ) => {
    const dispatch = useDispatch( VENDOR_QUOTES_STORE ) as DispatchActions;

    const [ search, setSearch ] = useState< string >( '' );
    const [ selectedQuotes, setSelectedQuotes ] = useState< number[] >( [] );
    const [ showConfirmation, setShowConfirmation ] =
        useState< boolean >( false );
    const [ modalActionType, setModalActionType ] = useState<
        'trash' | 'pending'
    >( 'trash' );
    const [ currentStatus, setCurrentStatus ] = useState<
        'all' | keyof typeof QUOTE_STATUS
    >( 'all' );

    // Define fields for DataViews
    const fields = [
        {
            id: 'id',
            label: __( 'Quote Id', 'dokan' ),
            enableHiding: false,
            render: ( { item } ) => {
                const quoteText = sprintf(
                    /* translators: %s: Quote id. */
                    __(
                        'Quote <strong class="whitespace-normal m-0">#%s</strong>',
                        'dokan'
                    ),
                    item.id
                );

                return (
                    <div className={ 'quote-info-container' }>
                        <DokanLink
                            className={ 'cursor-pointer' }
                            onClick={ () =>
                                navigate( '/requested-quotes/' + item?.id )
                            }
                        >
                            <span
                                dangerouslySetInnerHTML={ {
                                    __html: quoteText,
                                } }
                            />
                        </DokanLink>
                        { item.status === 'expired' && item.expiry_date && (
                            <div className="text-xs text-dokan-danger mt-2">
                                { sprintf(
                                    /* translators: %s: Expiry date. */
                                    __( '(Expired: %s)', 'dokan' ),
                                    item.expiry_date
                                ) }
                            </div>
                        ) }
                    </div>
                );
            },
        },
        {
            id: 'customer_name',
            enableHiding: false,
            enableSorting: false,
            label: __( 'Customer', 'dokan' ),
            render: ( { item } ) => (
                <Tooltip content={ item.customer_name }>
                    <p className="whitespace-normal m-0 tips">
                        { truncate( item.customer_name, 20 ) }
                    </p>
                </Tooltip>
            ),
        },
        {
            id: 'created_at',
            enableHiding: false,
            enableSorting: false,
            label: __( 'Date', 'dokan' ),
            render: ( { item } ) => (
                <p className="whitespace-normal m-0">{ item.created_at }</p>
            ),
        },
        {
            id: 'status',
            enableHiding: false,
            enableSorting: false,
            label: __( 'Status', 'dokan' ),
            render: ( { item } ) => (
                <QuoteStatus status={ item.status || QUOTE_STATUS?.PENDING } />
            ),
        },
    ];

    // Setup view state for DataViews
    const [ view, setView ] = useState( {
        perPage: 10,
        page: 1,
        search: '',
        type: 'table',
        titleField: 'id',
        status: currentStatus,
        layout: {
            table: {},
            grid: {},
            list: {},
            density: 'comfortable',
        },
        fields: fields.map( ( field ) =>
            field.id !== 'id' ? field.id : ''
        ),
    } );

    const [ quotedDate, setQuotedDate ] = useState( '' );

    const queryParams: QueryParams = useMemo(
        () => ( {
            search,
            status: currentStatus,
            page_no: view.page,
            per_page: view.perPage,
            order: view.sort?.direction || 'DESC',
            orderby: view.sort?.field || 'id',
            quoted_date: quotedDate,
        } ),
        [ search, quotedDate, currentStatus, view.page, view.perPage ]
    );

    const { quotes, headers, isLoading } = useSelect(
        ( select ) => ( {
            isLoading: select( VENDOR_QUOTES_STORE ).getLoading(),
            headers: select( VENDOR_QUOTES_STORE ).getHeaders(),
            quotes: select( VENDOR_QUOTES_STORE ).getQuotes( queryParams ),
        } ),
        [ queryParams ]
    );

    const refreshQuotes = ( args = {} ) => {
        try {
            dispatch.invalidateResolution( 'getQuotes', [
                {
                    ...queryParams,
                    ...args,
                },
            ] );
        } catch ( error ) {
            console.error( __( 'Quote refresh failed:', 'dokan' ), error );
        }
    };

    const handleStatusChange = ( status: typeof currentStatus ) => {
        setView( ( prev ) => ( {
            ...prev,
            page: 1,
        } ) );
        setCurrentStatus( status );
    };

    const handleBulkAction = async ( action: string ) => {
        try {
            const response = await dispatch.batchUpdateQuotes(
                action,
                selectedQuotes
            );
            if ( response?.success ) {
                setSelectedQuotes( [] );
                setShowConfirmation( false );
                refreshQuotes();
            }
        } catch ( error ) {
            console.error( __( 'Bulk action failed:', 'dokan' ), error );
        }
    };

    // Define actions for DataViews
    const actions = [];

    // @ts-ignore
    // Only add trash action when not in trash status
    if ( currentStatus !== 'trash' ) {
        actions.push( {
            id: 'view-quote',
            label: __( 'View', 'dokan' ),
            icon: () => (
                <span
                    className={ `px-2 bg-transparent font-medium dokan-link text-sm` }
                >
                    { __( 'Edit', 'dokan' ) }
                </span>
            ),
            isPrimary: true,
            callback: ( quoteList ) => {
                const quote = quoteList?.[ 0 ];
                navigate( `/requested-quotes/${ quote?.id }` );
            },
        } );
        actions.push( {
            id: 'trash-quote',
            label: __( 'Trash', 'dokan' ),
            icon: () => (
                <span
                    className={ `bg-transparent font-medium text-dokan-danger text-sm` }
                >
                    { __( 'Trash', 'dokan' ) }
                </span>
            ),
            isPrimary: true,
            supportsBulk: true,
            callback: ( quoteList ) => {
                setSelectedQuotes( [
                    ...selectedQuotes,
                    ...( quoteList?.map( ( quote ) => quote.id ) || [] ),
                ] );
                setModalActionType( QUOTE_STATUS?.TRASH );
                setShowConfirmation( true );
            },
        } );
    } else {
        actions.push( {
            id: 'pending-quote',
            label: __( 'Move to Pending', 'dokan' ),
            icon: () => (
                <span
                    className={ `bg-transparent font-medium dokan-link text-sm` }
                >
                    { __( 'Move to Pending', 'dokan' ) }
                </span>
            ),
            isPrimary: true,
            supportsBulk: true,
            callback: ( quoteList ) => {
                setSelectedQuotes( [
                    ...selectedQuotes,
                    ...( quoteList?.map( ( quote ) => quote.id ) || [] ),
                ] );
                setModalActionType( QUOTE_STATUS?.PENDING );
                setShowConfirmation( true );
            },
        } );
    }

    useEffect( () => {
        refreshQuotes();
    }, [ queryParams ] );

    useEffect( () => {
        // Update view when search changes from SearchBar component
        setView( ( prev ) => ( {
            ...prev,
            search,
            page: 1, // Reset to first page on new search
        } ) );
    }, [ search ] );

    // Modal content based on action type
    const modalConfig =
        modalActionType === 'trash'
            ? {
                  title: __( 'Trash Quote', 'dokan' ),
                  description: __(
                      'Are you sure you want to trash this quote? This action cannot be undone.',
                      'dokan'
                  ),
                  confirmText: __( 'Yes, Trashed', 'dokan' ),
                  onConfirm: () => handleBulkAction( QUOTE_STATUS.TRASH ),
              }
            : {
                  title: __( 'Move to Pending', 'dokan' ),
                  description: __(
                      'Are you sure you want to move this quote to pending? This will restore the quote for further actions.',
                      'dokan'
                  ),
                  confirmText: __( 'Yes, Move to Pending', 'dokan' ),
                  onConfirm: () => handleBulkAction( QUOTE_STATUS.PENDING ),
              };

    if ( isLoading ) {
        return <QuoteListSkeleton />;
    }

    if ( ! headers ) {
        return null;
    }

    return (
        <div className="space-y-4">
            { /* Before quote status tab slot */ }
            <Slot
                name={ QUOTE_LIST_SLOTS.BEFORE_STATUS_TABS }
                fillProps={ { quotes, queryParams } }
            />

            <StatusTabs
                headers={ headers }
                currentStatus={ currentStatus }
                onStatusChange={ handleStatusChange }
            />

            { /* After quote status tab slot */ }
            <Slot
                name={ QUOTE_LIST_SLOTS.AFTER_STATUS_TABS }
                fillProps={ { quotes, queryParams } }
            />

            <div className={ `quote-filter-container flex` }>
                <QuoteFilter
                    search={ search }
                    isLoading={ isLoading }
                    setSearch={ setSearch }
                    quotedDate={ quotedDate }
                    setQuotedDate={ setQuotedDate }
                    className="flex justify-end p-0"
                    setCurrentStatus={ setCurrentStatus }
                    isItemAvailable={ quotes?.length > 0 }
                />
            </div>

            { /* Before quote list table slot */ }
            <Slot
                name={ QUOTE_LIST_SLOTS.BEFORE_LIST_TABLE }
                fillProps={ { quotes, queryParams } }
            />

            { headers?.statusTotal > 0 || headers?.statusCounts?.trash > 0 ? (
                <>
                    <div className="dokan-request-quote-container">
                        { quotes?.length > 0 ? (
                            <div
                                className={ `relative overflow-x-auto shadow-md sm:rounded-lg` }
                            >
                                <DataViews
                                    data={ quotes || [] }
                                    namespace="dokan-request-quote"
                                    fields={ fields }
                                    actions={ actions }
                                    view={ view }
                                    onChangeView={ setView }
                                    selection={ selectedQuotes }
                                    onChangeSelection={ setSelectedQuotes }
                                    getItemId={ ( item ) => item.id }
                                    paginationInfo={ {
                                        totalItems: headers.total,
                                        totalPages: Math.ceil(
                                            headers.total / view.perPage
                                        ),
                                    } }
                                    isLoading={ isLoading }
                                />
                            </div>
                        ) : (
                            <EmptyQuote
                                message={ sprintf(
                                    /* translators: %s: Quote status */
                                    __( 'No %s quote found', 'dokan' ),
                                    STATUS_LABELS?.[
                                        currentStatus
                                    ]?.toLowerCase() || ''
                                ) }
                            />
                        ) }
                    </div>

                    <DokanModal
                        className="min-w-96"
                        loading={ isLoading }
                        isOpen={ showConfirmation }
                        onConfirm={ modalConfig.onConfirm }
                        confirmButtonText={ modalConfig.confirmText }
                        namespace="quote-action-confirmation"
                        dialogHeader={ modalConfig.title }
                        onClose={ () => setShowConfirmation( false ) }
                        confirmationTitle={ modalConfig.title }
                        confirmationDescription={ modalConfig.description }
                    />
                </>
            ) : (
                <EmptyQuote
                    shopUrl={ dokanRequestQuote?.wc_shop_url }
                    buttonText={ __( 'Go to shop', 'dokan' ) }
                />
            ) }

            { /* After quote list table slot */ }
            <Slot
                name={ QUOTE_LIST_SLOTS.AFTER_LIST_TABLE }
                fillProps={ { quotes, queryParams } }
            />
        </div>
    );
};

export default QuoteList;
